/**
 * Adminpanel – Benutzerverwaltung für STAFF und OWNER.
 *
 * Steht außerhalb des Partien-Rahmens (`Shell`), weil er an keine bestimmte
 * Partie gebunden ist – deshalb trägt die Seite ihre eigene, schlichte
 * Kopfzeile statt der Seitenleiste. Sonst dieselben Bausteine wie überall im
 * Betrieb (`DataTable`, `Card`, `Badge`), damit sich nichts fremd anfühlt, nur
 * weil man hier statt in einer Partie steht.
 *
 * STAFF kann die Liste einsehen; nur OWNER darf Rollen ändern und Konten
 * löschen.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  type Column,
  DataTable,
  Input,
  Modal,
  Notice,
  Select,
  Stat,
  StatGrid,
  StatusDot,
  type Tone,
} from '../components/ui';
import { api } from '../lib/api';
import { useAuth, useTheme } from '../store/auth';
import { num, sinceDate } from '../lib/format';

type User = {
  id: string;
  email: string;
  displayName: string;
  platformRole: string;
  emailVerifiedAt: string | null;
  createdAt: string;
  lastLoginAt: string | null;
  gamesCount: number;
};

const ROLE_LABEL: Record<string, string> = { USER: 'Benutzer', STAFF: 'Betrieb', OWNER: 'Inhaber' };
const ROLE_TONE: Record<string, Tone> = { USER: 'muted', STAFF: 'info', OWNER: 'success' };

export function Admin() {
  const currentUser = useAuth((s) => s.user);
  const isOwner = currentUser?.platformRole === 'OWNER';
  const { dark, toggle } = useTheme();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<User | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setUsers(await api.listUsers());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const changeRole = async (user: User, role: string) => {
    setSaving(user.id);
    try {
      await api.setUserRole(user.id, role);
      setUsers((prev) => prev?.map((u) => (u.id === user.id ? { ...u, platformRole: role } : u)) ?? prev);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteUser(pendingDelete.id);
      setUsers((prev) => prev?.filter((u) => u.id !== pendingDelete.id) ?? prev);
      setPendingDelete(null);
    } catch (e) {
      setDeleteError((e as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    if (!users) return [];
    const needle = search.trim().toLowerCase();
    if (!needle) return users;
    return users.filter((u) => u.displayName.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle));
  }, [users, search]);

  // Kennzahlen aus der ungefilterten Liste – die Suche soll nicht auch noch
  // die Übersicht darüber verändern, wie viele Konten es eigentlich gibt.
  const stats = useMemo(() => {
    if (!users) return null;
    return {
      total: users.length,
      unverified: users.filter((u) => !u.emailVerifiedAt).length,
      staff: users.filter((u) => u.platformRole !== 'USER').length,
      games: users.reduce((s, u) => s + u.gamesCount, 0),
    };
  }, [users]);

  const columns: Column<User>[] = [
    {
      key: 'name',
      header: 'Konto',
      sortValue: (u) => u.displayName,
      render: (u) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{u.displayName}</p>
          <p className="truncate text-2xs text-panel-500 dark:text-panel-400">{u.email}</p>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Rolle',
      sortValue: (u) => u.platformRole,
      render: (u) => <Badge tone={ROLE_TONE[u.platformRole] ?? 'muted'}>{ROLE_LABEL[u.platformRole] ?? u.platformRole}</Badge>,
    },
    {
      key: 'verified',
      header: 'Bestätigt',
      hideOnMobile: true,
      sortValue: (u) => (u.emailVerifiedAt ? 1 : 0),
      render: (u) => (u.emailVerifiedAt ? <StatusDot tone="success" label="Bestätigt" /> : <StatusDot tone="warning" label="Ausstehend" />),
    },
    {
      key: 'createdAt',
      header: 'Angelegt',
      hideOnMobile: true,
      sortValue: (u) => u.createdAt,
      render: (u) => <span className="text-xs text-panel-500 dark:text-panel-400">{sinceDate(u.createdAt)}</span>,
    },
    {
      key: 'lastLoginAt',
      header: 'Letzter Login',
      hideOnMobile: true,
      sortValue: (u) => u.lastLoginAt ?? '',
      render: (u) => <span className="text-xs text-panel-500 dark:text-panel-400">{u.lastLoginAt ? sinceDate(u.lastLoginAt) : '–'}</span>,
    },
    {
      key: 'games',
      header: 'Partien',
      align: 'right',
      sortValue: (u) => u.gamesCount,
      render: (u) => <span className="tabular text-xs">{u.gamesCount}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: isOwner ? '15rem' : '1px',
      render: (u) =>
        !isOwner ? null : u.id === currentUser?.id ? (
          <span className="text-xs text-panel-400">Du</span>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <Select
              value={u.platformRole}
              onChange={(e) => void changeRole(u, e.target.value)}
              className="h-7 py-0 text-xs"
              disabled={saving === u.id}
              aria-label={`Rolle von ${u.displayName}`}
            >
              <option value="USER">Benutzer</option>
              <option value="STAFF">Betrieb</option>
              <option value="OWNER">Inhaber</option>
            </Select>
            {saving === u.id && <span className="text-xs text-panel-400">…</span>}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setDeleteError(null);
                setPendingDelete(u);
              }}
              className="text-panel-400 hover:bg-red-500/10 hover:text-red-500"
            >
              Löschen
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="min-h-full">
      <header className="border-b divider">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            ← Partien
          </Button>
          <button
            type="button"
            onClick={toggle}
            aria-label="Farbschema wechseln"
            className="rounded-md px-2 py-1.5 text-sm text-panel-500 transition-colors hover:bg-panel-200/70 dark:text-panel-400 dark:hover:bg-panel-800"
          >
            {dark ? '☀' : '☾'}
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {isOwner && (
          <div className="mb-4">
            <Link
              to="/admin/games"
              className="inline-flex items-center gap-1.5 rounded-md border border-panel-300 bg-white px-3 py-1.5 text-sm font-medium text-panel-800 hover:bg-panel-100 dark:border-panel-700 dark:bg-panel-800 dark:text-panel-100 dark:hover:bg-panel-700"
            >
              Partien verwalten →
            </Link>
          </div>
        )}

        <PageHeaderBlock
          isOwner={isOwner}
          search={search}
          onSearch={setSearch}
        />

        {error && (
          <div className="mb-4">
            <Notice tone="danger">{error}</Notice>
          </div>
        )}

        {stats && (
          <div className="mb-4">
            <StatGrid cols={4}>
              <Stat label="Konten" value={num(stats.total)} />
              <Stat label="Unbestätigt" value={num(stats.unverified)} tone={stats.unverified > 0 ? 'warning' : 'neutral'} />
              <Stat label="Betrieb" value={num(stats.staff)} hint="Betrieb + Inhaber" />
              <Stat label="Partien gesamt" value={num(stats.games)} />
            </StatGrid>
          </div>
        )}

        <Card padded={false}>
          <DataTable
            columns={columns}
            rows={filtered}
            rowKey={(u) => u.id}
            empty={
              users === null
                ? 'Wird geladen …'
                : search
                  ? 'Keine Konten passen zur Suche.'
                  : 'Keine Konten gefunden.'
            }
          />
        </Card>
      </div>

      <Modal
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Konto löschen"
        footer={
          <>
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              Abbrechen
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => void confirmDelete()}>
              Endgültig löschen
            </Button>
          </>
        }
      >
        <div className="space-y-3 text-sm">
          <p>
            Soll <strong>{pendingDelete?.displayName}</strong> ({pendingDelete?.email}) endgültig gelöscht werden?
            Das lässt sich nicht rückgängig machen.
          </p>
          {!!pendingDelete?.gamesCount && (
            <div className="surface-muted p-3 text-xs text-panel-600 dark:text-panel-300">
              Das Konto besitzt {pendingDelete.gamesCount} {pendingDelete.gamesCount === 1 ? 'Partie' : 'Partien'} – die
              werden mit allen Spielständen mitgelöscht.
            </div>
          )}
          {deleteError && <Notice tone="danger">{deleteError}</Notice>}
        </div>
      </Modal>
    </div>
  );
}

function PageHeaderBlock({
  isOwner,
  search,
  onSearch,
}: {
  isOwner: boolean;
  search: string;
  onSearch: (value: string) => void;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold">Benutzerverwaltung</h1>
        <p className="mt-0.5 text-sm text-panel-500 dark:text-panel-400">
          {isOwner ? 'Rollen zuweisen, Konten einsehen und löschen.' : 'Konten einsehen (nur Inhaber ändern Rollen oder löschen).'}
        </p>
      </div>
      <Input placeholder="Suchen …" value={search} onChange={(e) => onSearch(e.target.value)} className="w-56" />
    </div>
  );
}
